import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import useMobileDetect from 'use-mobile-detect-hook'

import { Paths } from '../../config/routes'
import { useDebounce } from '../../hooks/use-debounce'
import { Card } from '../card'
import { DatePicker } from '../date-picker'
import {
    TransactionCryptoItem,
    TransactionItem,
    TransactionItemSkeleton,
} from '../transaction-item'
import { BackIcon } from './icons'
import Icon from './icons/truck.svg'
import PlaceholderIcon from './placeholder'

export interface TransactionListProps {
    /** Transactions list */
    transactions?: any[]
    /** account list */
    accounts?: string[]
    /** Loading state */
    loading?: boolean
    /** Optional classnames */
    className?: string
    /** This makes the component grow vertically */
    fillVertical?: boolean
    /** Next page handler, this will update content */
    nextPage?: () => void
    /** This will define transference type */
    type?: 'crypto' | 'bank'
    /** Filter account list for transactions */
    filter?: string[] | null[]
    /** Filter ticker list for transactions */
    providers?: string | undefined
    /** Show placeholder*/
    showPlaceholder?: boolean
    /** Hide next button if not needed */
    hideNext?: boolean
    /** Dates for filters */
    date?: any | undefined
    setDate?: (date: any) => void
    /** Next page handler, this will update content */
    exportTransactions?: () => void
    /** Selected account for title display */
    selectedAccount?: any | null

    // Indicator of mobile
    mobile?: boolean
}

/**
 * Default sub menu component for click interactions
 */
export function TransactionList({
    transactions = [],
    loading,
    className,
    fillVertical,
    nextPage,
    hideNext = false,
    showPlaceholder,
    type,
    date,
    setDate,
    exportTransactions,
    selectedAccount,
}: TransactionListProps) {
    const pageLength = 10
    const detectMobile = useMobileDetect()

    const debouncedLoading = useDebounce({ value: loading, delay: 500 })

    const transactionListClasses = [
        'flex flex-col flex-grow justify-start',
        'h-full',
    ].join(' ')
    const headerClassNames =
        'not-italic font-light text-sm leading-3 text-gray pb-4'

    const tableClasses = [
        `flex flex-col justify-start gap-y-2 overflow-scroll flex-grow scrollbar-hide`,
    ].join(' ')

    const headers =
        type === 'crypto' ? (
            <tr className='text-left'>
                <th className={headerClassNames}>Asset</th>
                <th className={headerClassNames}>Type</th>
                <th className={headerClassNames}>Date</th>
                <th className={headerClassNames}>Impact</th>
                <th className={`${headerClassNames} text-right`}>
                    Asset value
                </th>
                <th className={`${headerClassNames} text-right`}>USD Amount</th>
            </tr>
        ) : (
            <tr className='text-left'>
                <th className={headerClassNames}>Description</th>
                {detectMobile.isDesktop() && (
                    <th className={headerClassNames}>Type</th>
                )}
                <th className={`${headerClassNames} text-right`}>Amount</th>
            </tr>
        )

    const title = useMemo(() => {
        if (selectedAccount) {
            return (
                <div className='flex flex-row items-center gap-2 mb-2'>
                    <Link to={Paths.ACCOUNTS}>
                        <BackIcon />
                    </Link>
                    <h2 className='m-0 whitespace-nowrap'>
                        {selectedAccount?.name}
                    </h2>{' '}
                    -{' '}
                    <p className='m-0 font-light text-md text-gray6'>
                        {selectedAccount?.amount?.currency}
                    </p>
                </div>
            )
        } else {
            return <h2>Transaction history</h2>
        }
    }, [selectedAccount])
    return (
        <Card className={`${transactionListClasses} ${className}`}>
            <div className='flex flex-col items-start justify-between w-full sm:items-center sm:flex-row'>
                {title}

                {fillVertical && (
                    <DatePicker value={date} onChange={setDate as any} />
                )}
            </div>
            <div className={tableClasses}>
                <table className='w-full mt-4 table-auto'>
                    <thead>{!showPlaceholder ? headers : null}</thead>
                    <tbody data-test-id='transaction-items-table'>
                        {showPlaceholder ? (
                            <tr>
                                <td
                                    className='py-8 pt-56 text-center'
                                    colSpan={6}>
                                    <div className='flex flex-row justify-center w-full mb-4'>
                                        <PlaceholderIcon />
                                    </div>
                                    Pick one account group
                                </td>
                            </tr>
                        ) : transactions && transactions.length ? (
                            transactions.map((t, idx) =>
                                type === 'crypto' ? (
                                    <TransactionCryptoItem
                                        {...t}
                                        key={`transaction-${idx}`}
                                        icon={t.icon}
                                    />
                                ) : (
                                    <TransactionItem
                                        {...t}
                                        key={`transaction-${idx}`}
                                        icon={
                                            type === 'bank' ? (
                                                <img
                                                    className='box-border w-6 h-6 p-1 mr-4 rounded bg-gray5'
                                                    src={Icon}
                                                    alt={'truck_icon'}
                                                />
                                            ) : (
                                                t.icon
                                            )
                                        }
                                    />
                                ),
                            )
                        ) : (
                            !debouncedLoading && (
                                <tr>
                                    <td
                                        className='py-8 mt-4 text-center'
                                        colSpan={5}>
                                        No transactions found
                                    </td>
                                </tr>
                            )
                        )}

                        {debouncedLoading && !showPlaceholder && (
                            <>
                                {Array(
                                    pageLength -
                                        (transactions.length % pageLength),
                                )
                                    .fill(0)
                                    .map((i, idx) => (
                                        <TransactionItemSkeleton
                                            type={type}
                                            key={`item-skeleton-${idx + i}`}
                                        />
                                    ))}
                            </>
                        )}
                    </tbody>
                </table>
            </div>

            <div
                className={`w-full flex flex-row mt-2 justify-${
                    fillVertical ? 'between' : 'center'
                }`}>
                {fillVertical ? (
                    <>
                        <h3
                            onClick={exportTransactions}
                            className='m-0 text-center cursor-pointer'>
                            Export transactions
                        </h3>
                        {!hideNext && !loading && !showPlaceholder && (
                            <div onClick={nextPage} className='cursor-pointer'>
                                <h3 className='m-0 text-center'>Load more</h3>
                            </div>
                        )}
                    </>
                ) : (
                    <div className='flex flex-row justify-center w-full'>
                        <Link to={Paths.ACCOUNTS}>
                            <h3 className='m-0 text-center'>View More</h3>
                        </Link>
                    </div>
                )}
            </div>
        </Card>
    )
}
